/**
 * Environment detection utilities for Lovable Preview mode
 *
 * Lovable Preview runs without authentication and uses dummy data
 * Production mode requires authentication and uses real backend
 */

/**
 * Detects if the app is running in Lovable Preview mode
 * Lovable Preview runs without authentication and uses dummy data
 */
const PREVIEW_OVERRIDE_KEY = 'coffeecrm_preview_override';
// Deliberate in-app toggle ("Switch to Preview" / "Switch to Production").
// Distinct from PREVIEW_OVERRIDE_KEY, which is seeded by a ?preview= query
// param and whose persisted `true` is treated as stray residue and dropped.
// A value here is an explicit user choice, so it is honored persistently.
const PREVIEW_MODE_KEY = 'coffeecrm_preview_mode';

function getHostnameFlags() {
  let hostname = '';
  let hasLovableProject = false;
  let hasLovableDev = false;
  let hasLovableApp = false;
  let isProductionDomain = false;

  if (typeof window !== 'undefined' && window.location?.hostname) {
    hostname = window.location.hostname;
    hasLovableProject = hostname.includes('lovableproject.com');
    hasLovableDev = hostname.includes('lovable.dev');
    hasLovableApp = hostname.includes('lovable.app');
    isProductionDomain = hostname === 'coffeecrm.lovable.app';
  }

  return {
    hostname,
    hasLovableProject,
    hasLovableDev,
    hasLovableApp,
    isProductionDomain,
    isHostedPreview: hasLovableProject || hasLovableDev || (hasLovableApp && !isProductionDomain),
  };
}

/**
 * Check for per-user preview override via ?preview=true/false query param.
 * The query param seeds localStorage so it persists across navigations.
 */
function getPreviewOverride(): { value: boolean | null; fromQueryParam: boolean } {
  if (typeof window === 'undefined') return { value: null, fromQueryParam: false };

  // Query param takes precedence and seeds localStorage
  const params = new URLSearchParams(window.location.search);
  const paramValue = params.get('preview');
  if (paramValue === 'true' || paramValue === 'false') {
    localStorage.setItem(PREVIEW_OVERRIDE_KEY, paramValue);
    return { value: paramValue === 'true', fromQueryParam: true };
  }

  // Fall back to localStorage
  const stored = localStorage.getItem(PREVIEW_OVERRIDE_KEY);
  if (stored === 'true') return { value: true, fromQueryParam: false };
  if (stored === 'false') return { value: false, fromQueryParam: false };

  return { value: null, fromQueryParam: false };
}

export const isLovablePreview = (): boolean => {
  const { isHostedPreview } = getHostnameFlags();

  // Preview hosts should always stay in preview mode. A persisted localStorage
  // override can otherwise trap users on /login in preview, where Auth0 is not
  // meant to be the normal editing flow.
  if (isHostedPreview) return true;

  const hasLovablePreviewEnv = import.meta.env.VITE_LOVABLE_PREVIEW === 'true';
  const hasDisableAuthEnv = import.meta.env.VITE_DISABLE_AUTH === 'true';
  const envPreview = hasLovablePreviewEnv || hasDisableAuthEnv;

  // An explicit ?preview=true/false on THIS navigation wins outright (e.g. a
  // dev inspecting prod). getPreviewOverride() also seeds PREVIEW_OVERRIDE_KEY.
  const { value: override, fromQueryParam } = getPreviewOverride();
  if (fromQueryParam) return override === true;

  // Deliberate in-app toggle ("Switch to Preview" / "Switch to Production").
  // This is an explicit user action, so a persisted value is honored as-is —
  // unlike a stray persisted query-param override below.
  if (typeof window !== 'undefined') {
    const mode = localStorage.getItem(PREVIEW_MODE_KEY);
    if (mode === 'true') return true;
    if (mode === 'false') return false;
  }

  // Persisted query-param override (not from this navigation): an explicit
  // `false` is honored, but a PERSISTED `true` must never silently force
  // preview — i.e. dummy "Gazillion" data — onto a real-backend host. Without
  // this guard, one stray ?preview=true visit traps the user in dummy mode
  // permanently, cross-contaminating real workspace data (e.g. the Settings >
  // Visitor ID website URL). Drop the stale flag unless an env flag is set.
  if (override === false) return false;
  if (override === true) {
    if (envPreview) return true;
    localStorage.removeItem(PREVIEW_OVERRIDE_KEY);
    return false;
  }

  // No override: at this point every hosted-preview hostname has already
  // returned above, so build-time env flags are the only preview trigger.
  return envPreview;
};

/**
 * Set (or clear) the deliberate in-app preview toggle. Pass a boolean to force
 * preview on/off persistently; pass null to revert to automatic detection.
 */
export const setPreviewMode = (enabled: boolean | null): void => {
  if (typeof window === 'undefined') return;
  if (enabled === null) {
    localStorage.removeItem(PREVIEW_MODE_KEY);
  } else {
    localStorage.setItem(PREVIEW_MODE_KEY, String(enabled));
  }
};

/**
 * Clear the per-user preview override (reverts to automatic detection).
 */
export const clearPreviewOverride = (): void => {
  if (typeof window !== 'undefined') {
    localStorage.removeItem(PREVIEW_OVERRIDE_KEY);
    localStorage.removeItem(PREVIEW_MODE_KEY);
  }
};

/**
 * Detects if the app is running in production with real backend
 */
export const isProduction = (): boolean => {
  return !isLovablePreview() && import.meta.env.MODE === 'production';
};

/**
 * Detects if the app is running in development with real backend
 */
export const isDevelopment = (): boolean => {
  return !isLovablePreview() && import.meta.env.MODE === 'development';
};

/**
 * Rewrite a recording/clip URL so it plays in LOCAL dev only.
 *
 * Locally the backend stores S3 URLs using the real AWS host
 * (`{bucket}.s3.{region}.amazonaws.com`), but the bytes actually live in
 * localstack, which the browser can't reach at that host — so `<video>`
 * renders black. Here we point such URLs at the localstack S3 endpoint.
 *
 * This is strictly a local-dev convenience: the whole body is behind
 * `import.meta.env.DEV`, which Vite statically replaces with `false` in
 * production builds, so this rewrite is dead-code-eliminated from the
 * deployed bundle and can never affect real users.
 */
export const localizeMediaUrl = (url?: string | null): string | undefined => {
  if (!url) return url ?? undefined;
  if (!import.meta.env.DEV) return url; // compiled out of production builds
  try {
    const u = new URL(url);
    const match = u.hostname.match(/^(.+)\.s3\.[^.]+\.amazonaws\.com$/);
    if (!match) return url; // not an AWS S3 URL — leave untouched
    const bucket = match[1];
    return `http://${bucket}.s3.localhost.localstack.cloud:4566${u.pathname}${u.search}`;
  } catch {
    return url;
  }
};
